import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  noIndex?: boolean;
  schema?: unknown;
  ogType?: string;
}

function siteOrigin(): string {
  return (import.meta.env.VITE_SITE_URL || '').replace(/\/$/, '');
}

function absoluteUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${siteOrigin()}${path}`;
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  noIndex = false,
  schema,
  ogType = 'website',
}: SEOProps) {
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // Set a meta tag (name or property), reusing an existing tag when present so
    // there is always exactly one managed tag per key. The previous value is
    // recorded and restored on cleanup so metadata never leaks between routes.
    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        cleanups.push(() => el.remove());
      } else {
        const previous = el.getAttribute('content');
        cleanups.push(() => {
          if (previous === null) el.remove();
          else el.setAttribute('content', previous);
        });
      }
      el.setAttribute('content', content);
    };

    // Title
    if (title) {
      const previousTitle = document.title;
      document.title = title;
      cleanups.push(() => {
        document.title = previousTitle;
      });
    }

    // Basic meta
    if (description) setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    if (noIndex) setMeta('name', 'robots', 'noindex, nofollow');

    // Canonical
    if (canonical) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
        cleanups.push(() => link.remove());
      } else {
        const previous = link.getAttribute('href');
        cleanups.push(() => {
          if (previous === null) link.remove();
          else link.setAttribute('href', previous);
        });
      }
      link.setAttribute('href', absoluteUrl(canonical));
    }

    // Open Graph
    if (title) setMeta('property', 'og:title', title);
    if (description) setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    if (canonical) setMeta('property', 'og:url', absoluteUrl(canonical));

    // Twitter
    setMeta('property', 'twitter:card', 'summary_large_image');
    if (title) setMeta('property', 'twitter:title', title);
    if (description) setMeta('property', 'twitter:description', description);
    if (canonical) setMeta('property', 'twitter:url', absoluteUrl(canonical));

    // Schema.org JSON-LD — kept separate from the meta tag mutation above.
    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      cleanups.push(() => script.remove());
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [title, description, keywords, canonical, noIndex, schema, ogType]);

  return null;
}